import { Minus, Plus, AlertCircle } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { SHOP } from "@/constants/testIds";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  signature: "SIGNATURE",
  cocktail: "COCKTAIL",
  beer: "BIÈRE",
  energy: "ENERGY",
  soft: "SOFT",
  juice: "JUS",
  water: "EAU",
  food: "FOOD",
  ecocup: "ECOCUP",
};

export default function ProductCard({ product }) {
  const { add } = useCart();
  const [qty, setQty] = useState(1);

  const remaining = product.stock_remaining;
  const lowStock = remaining > 0 && remaining <= Math.max(8, Math.floor(product.stock_initial * 0.15));
  const soldOut = remaining <= 0;
  const featured = product.featured;

  const onAdd = () => {
    if (soldOut) return;
    add(product, qty);
    toast.success(`${qty} × ${product.name} ajouté${qty > 1 ? "s" : ""}`);
  };

  return (
    <article
      data-testid={SHOP.productCard(product.id)}
      className={`group relative border-2 border-[#14230A] bg-[#FDF0DF] card-lift flex flex-col overflow-hidden ${
        soldOut ? "opacity-60" : ""
      }`}
    >
      {soldOut && (
        <div
          data-testid={`product-sold-out-ribbon-${product.id}`}
          className="absolute top-3 right-[-40px] rotate-45 z-20 bg-[#C53A1B] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-[0.3em] px-10 py-1 shadow-md pointer-events-none"
        >
          Sold out
        </div>
      )}
      {/* HOVER IMAGE — appears behind text with green overlay */}
      {product.image_url && (
        <>
          <img
            src={product.image_url}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          />
          <div className="absolute inset-0 bg-[#14230A] opacity-0 group-hover:opacity-80 transition-opacity duration-500 pointer-events-none" />
        </>
      )}

      {/* Top strip */}
      <div className="relative flex items-stretch border-b-2 border-[#14230A] group-hover:border-[#FDF0DF] transition-colors duration-300 z-10">
        <div className="flex-1 px-4 py-2 font-cond uppercase text-[11px] tracking-[0.25em] flex items-center gap-2 group-hover:text-[#FDF0DF] transition-colors">
          <span>{CATEGORY_LABEL[product.category] || product.category}</span>
          {product.is_alcohol && (
            <span className="text-[10px] px-1.5 py-0.5 bg-[#14230A] text-[#FDF0DF] group-hover:bg-[#FDF0DF] group-hover:text-[#14230A] transition-colors">
              18+
            </span>
          )}
        </div>
        {featured && (
          <div className="px-3 py-2 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-[11px] tracking-[0.2em] border-l-2 border-[#14230A] group-hover:border-[#FDF0DF] transition-colors">
            ★ Maison
          </div>
        )}
      </div>

      {/* Body */}
      <div className="relative p-5 flex flex-col flex-1 z-10 group-hover:text-[#FDF0DF] transition-colors duration-300">
        <h3 className="font-display text-2xl md:text-3xl leading-[0.95]">{product.name}</h3>
        {product.subtitle && (
          <div className="mt-1 font-cond text-[10px] tracking-[0.3em] uppercase text-[#FF4D00]">
            {product.subtitle}
          </div>
        )}
        <p className="mt-3 text-sm opacity-75 leading-snug min-h-[40px]">{product.description}</p>

        {/* Price + unit */}
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-display text-4xl">{product.price_eur.toFixed(0)}</span>
          <span className="font-cond text-sm tracking-widest opacity-70">€ / {product.unit_label}</span>
        </div>

        {/* Stock indicator */}
        <div className="mt-3">
          {soldOut ? (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#14230A] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-[0.25em] group-hover:bg-[#FDF0DF] group-hover:text-[#14230A] transition-colors">
              <AlertCircle className="w-3 h-3" /> Sold out
            </div>
          ) : lowStock ? (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-[0.25em]">
              Plus que {remaining}
            </div>
          ) : (
            <div className="font-cond uppercase text-[10px] tracking-[0.25em] opacity-60">
              {remaining} disponibles
            </div>
          )}
        </div>

        {/* Qty + Add */}
        <div className="mt-auto pt-5 flex items-stretch gap-2">
          <div className="flex items-center border-2 border-[#14230A] group-hover:border-[#FDF0DF] transition-colors shrink-0">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              disabled={soldOut}
              className="w-8 sm:w-9 h-11 flex items-center justify-center hover:bg-[#FF4D00] hover:text-[#FDF0DF] transition-colors"
              aria-label="Diminuer"
            >
              <Minus className="w-4 h-4" />
            </button>
            <input
              data-testid={SHOP.productQtyInput(product.id)}
              type="number"
              min={1}
              max={remaining}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(remaining, parseInt(e.target.value) || 1)))}
              className="w-9 sm:w-12 h-11 text-center bg-transparent font-cond font-bold text-base sm:text-lg outline-none border-x-2 border-[#14230A] group-hover:border-[#FDF0DF] transition-colors"
            />
            <button
              type="button"
              onClick={() => setQty((q) => Math.min(remaining, q + 1))}
              disabled={soldOut}
              className="w-8 sm:w-9 h-11 flex items-center justify-center hover:bg-[#FF4D00] hover:text-[#FDF0DF] transition-colors"
              aria-label="Augmenter"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <button
            data-testid={SHOP.productAddBtn(product.id)}
            onClick={onAdd}
            disabled={soldOut}
            className={`flex-1 min-w-[120px] h-11 px-3 font-cond uppercase tracking-[0.1em] sm:tracking-[0.18em] text-sm font-bold transition-all whitespace-nowrap ${
              soldOut
                ? "bg-[#14230A]/20 text-[#14230A]/40 cursor-not-allowed"
                : "bg-[#14230A] text-[#FDF0DF] group-hover:bg-[#FF4D00] hover:bg-[#FF4D00]"
            }`}
          >
            {soldOut ? "Sold out" : "Ajouter au panier"}
          </button>
        </div>
      </div>
    </article>
  );
}
